import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { InquiryDetails } from '@models/inquiries';

import { handleGetSharedInquiryResponse, handleShareInquiryResponse } from './actions';
import { IInquiriesShareState } from './slice.interface';

const initialState: IInquiriesShareState = {
  inquiryToShare: null,
  shareSelectedSupplements: [],
  shareUrl: null,
  sharedResponse: null,
  isLoading: false,
  error: false,
};

export const inquiriesShareSlice = createSlice({
  name: 'inquiries-share',
  initialState,
  reducers: {
    setInquiryToShare: (
      state: IInquiriesShareState,
      action: PayloadAction<InquiryDetails | null>,
    ) => {
      state.inquiryToShare = action.payload;
    },

    changeAllSupplements: (state: IInquiriesShareState, action: PayloadAction<boolean>) => {
      if (state.inquiryToShare) {
        state.shareSelectedSupplements = action.payload
          ? state.inquiryToShare.supplementInquiriesDto.reduce((prev, item) => {
              if (item.isActive) {
                prev.push(item.supplementInquiryId);
              }
              return prev;
            }, [] as number[])
          : [];
      }
    },

    changeAllMedications: (state: IInquiriesShareState, action: PayloadAction<boolean>) => {
      if (state.inquiryToShare) {
        state.shareSelectedSupplements = action.payload
          ? state.inquiryToShare.medicationInquiriesDto.map(item => item.medicationInquiryId)
          : [];
      }
    },

    updateListOfItems: (
      state: IInquiriesShareState,
      action: PayloadAction<{ value: number; checked: boolean }>,
    ) => {
      if (state.inquiryToShare) {
        state.shareSelectedSupplements = action.payload.checked
          ? [...state.shareSelectedSupplements, action.payload.value]
          : state.shareSelectedSupplements.filter(item => item !== action.payload.value);
      }
    },

    setShareSelectedSupplements: (state: IInquiriesShareState, action: PayloadAction<number[]>) => {
      state.shareSelectedSupplements = action.payload;
    },

    removeSharedInquiryResponseDetails: (state: IInquiriesShareState) => {
      state.inquiryToShare = null;
      state.shareSelectedSupplements = [];
      state.sharedResponse = null;
      state.shareUrl = null;
      state.isLoading = false;
      state.error = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(handleShareInquiryResponse.pending, (state: IInquiriesShareState, action) => {
        state.shareUrl = null;
        state.isLoading = true;
        state.error = false;
      })
      .addCase(handleShareInquiryResponse.rejected, (state: IInquiriesShareState) => {
        state.isLoading = false;
        state.error = true;
      })
      .addCase(handleShareInquiryResponse.fulfilled, (state: IInquiriesShareState, action) => {
        state.shareUrl = action.payload.data.sharedInquiryUrl;
        state.isLoading = false;
      })
      .addCase(handleGetSharedInquiryResponse.pending, (state: IInquiriesShareState) => {
        state.isLoading = true;
        state.error = false;
      })
      .addCase(handleGetSharedInquiryResponse.rejected, (state: IInquiriesShareState) => {
        state.isLoading = false;
        state.error = true;
      })
      .addCase(handleGetSharedInquiryResponse.fulfilled, (state: IInquiriesShareState, action) => {
        state.sharedResponse = action.payload.data;
        state.isLoading = false;
      });
  },
});

export const {
  setInquiryToShare,
  changeAllSupplements,
  changeAllMedications,
  updateListOfItems,
  setShareSelectedSupplements,
  removeSharedInquiryResponseDetails,
} = inquiriesShareSlice.actions;

export default inquiriesShareSlice.reducer;
